import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import ArrowIcon from 'assets/images/ambassador/arrow-right.svg';
import RolePhoto from 'assets/images/ambassador/role-photo.jpg';
import AcademyPlayer from 'components/atoms/academy_player';
import AmbassadorJoinConfirm from 'components/molecules/ambassador_join_confirm';
import AmbassadorWelcomeModal from 'components/molecules/ambassador_welcome_modal';
import { acceptAmbassadorInvitation } from 'services/ambassador';
import { track } from 'services/tracking';
import { ApplicationState } from 'store';
import { refreshProfile } from 'store/modules/profile_store/actions';
import { ProfileState } from 'store/modules/profile_store/types';
import PoppinsFont from 'styles/poppins_font';
import { Container } from './styles';

/**
 * URL de incorporação do vídeo de apresentação do programa
 * (ex.: https://player.vimeo.com/video/123). Vazia, o quadro avisa que o vídeo vem em breve.
 */
const INVITATION_VIDEO_URL = '';

const BENEFITS = [
  {
    title: 'Acesso antecipado',
    text: 'Conheça e teste novas ferramentas antes do lançamento para toda a base de clientes.',
  },
  {
    title: 'Contato direto',
    text: 'Converse com os executivos e com a equipe responsável pela evolução da plataforma.',
  },
  {
    title: 'Encontros exclusivos de capacitação',
    text: 'Participe de encontros exclusivos para aprofundar seus conhecimentos, aprender a utilizar as ferramentas e esclarecer dúvidas com a nossa equipe.',
  },
  {
    title: 'Condições especiais em eventos e capacitações',
    text: 'Tenha prioridade de inscrição ou descontos em eventos e cursos selecionados para ampliar seus conhecimentos e desenvolver sua atuação nas licitações.',
  },
  {
    title: 'Certificação na metodologia exclusiva do ConLicitação',
    text: 'O ConLicitação lançará uma metodologia de vendas para o governo, e você terá a oportunidade de se capacitar para aplicá-la na sua rotina profissional e obter a certificação.',
  },
  {
    title: 'Sorteios exclusivos',
    text: 'Os embaixadores mais engajados poderão participar de sorteios periódicos, em reconhecimento à participação nos testes e às contribuições para o desenvolvimento das soluções.',
  },
  {
    title: 'Comunidade exclusiva de embaixadores',
    text: 'Participe de um espaço dedicado à troca de experiências, ao compartilhamento de sugestões e à conexão com outros profissionais e com a equipe do ConLicitação.',
  },
  {
    title: 'Participação nas lives de lançamento',
    text: 'Cada nova ferramenta terá uma live de lançamento. Os embaixadores mais engajados poderão ser convidados a participar dessas transmissões para compartilhar suas experiências com a solução e suas contribuições durante os testes.',
  },
];

const ROLES = [
  {
    title: 'Testar as ferramentas',
    text: 'Use os recursos antecipadamente em situações da sua rotina.',
  },
  {
    title: 'Compartilhar sua experiência',
    text: 'Conte o que funcionou e indique pontos que podem melhorar.',
  },
  {
    title: 'Participar da comunidade',
    text: 'Acompanhe os testes e converse diretamente com a equipe.',
  },
];

type AcceptStatus = 'idle' | 'sending' | 'error';

const AmbassadorInvitation: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [acceptStatus, setAcceptStatus] = useState<AcceptStatus>('idle');
  const [accepted, setAccepted] = useState(false);
  const [communityUrl, setCommunityUrl] = useState<string | null>(null);

  const isMember = Boolean(profile.ambassador?.member);
  const isInvited = Boolean(profile.ambassador?.invited);

  useEffect(() => {
    document.title = 'Seja um Embaixador - ConLicitação';
    track('embaixador.abriu_convite');
  }, []);

  // Depois do aceite a página continua aberta por baixo do modal de boas-vindas.
  if (!accepted && (!isInvited || isMember)) {
    return <Redirect to="/painel" />;
  }

  const openConfirm = () => {
    setAcceptStatus('idle');
    setConfirmOpen(true);
  };

  const handleAccept = async () => {
    if (acceptStatus === 'sending') return;
    setAcceptStatus('sending');

    const result = await acceptAmbassadorInvitation().catch(() => null);
    if (!result) {
      setAcceptStatus('error');
      return;
    }

    setCommunityUrl(result.communityUrl);
    setConfirmOpen(false);
    setAccepted(true);
    setAcceptStatus('idle');
    dispatch(refreshProfile());
  };

  const handleCloseWelcome = () => history.push('/painel');

  const acceptButton = (
    <div className="accept-action">
      <button type="button" className="accept-button" onClick={openConfirm} disabled={accepted}>
        Entrar para o time de embaixadores
        <img src={ArrowIcon} alt="" />
      </button>
    </div>
  );

  return (
    <Container className="page-container">
      <PoppinsFont />

      <section className="hero">
        <div className="hero-text">
          <h1>Seja um cliente Embaixador do ConLicitação</h1>
          <p>
            Teste novas ferramentas antes do lançamento e compartilhe sua experiência diretamente
            com a equipe do ConLicitação.
          </p>
        </div>
        <div className="hero-video">
          {INVITATION_VIDEO_URL ? (
            <AcademyPlayer
              src={INVITATION_VIDEO_URL}
              title="Vídeo de apresentação do programa de embaixadores"
            />
          ) : (
            <div className="hero-video-empty">
              <i className="material-icons">ondemand_video</i>
              <span>O vídeo de apresentação estará disponível em breve.</span>
            </div>
          )}
        </div>
      </section>
      <div className="green-bar hero-bar" />

      <section className="benefits">
        <h2>Sua experiência orienta as próximas evoluções.</h2>
        <p className="section-subtitle">Veja os benefícios de um embaixador:</p>
        <ol className="benefit-list">
          {BENEFITS.map((benefit, index) => (
            <li key={benefit.title} className="benefit-card">
              <span className="benefit-number">{`0${index + 1}.`}</span>
              <strong>{benefit.title}</strong>
              <p>{benefit.text}</p>
            </li>
          ))}
        </ol>
        {acceptButton}
      </section>

      <section className="roles">
        <img className="roles-photo" src={RolePhoto} alt="" />
        <div className="roles-content">
          <h2>O seu papel para o sucesso do projeto:</h2>
          <ul>
            {ROLES.map((role) => (
              <li key={role.title}>
                <strong>{role.title}</strong>
                <span>{role.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <div className="green-bar roles-bar" />

      <section className="join">
        <h2>Quer fazer parte deste time?</h2>
        <p>
          Ao confirmar sua participação, você poderá acessar a comunidade exclusiva no WhatsApp e
          acompanhar os próximos testes.
        </p>
        {acceptButton}
      </section>

      <AmbassadorJoinConfirm
        show={confirmOpen && !accepted}
        sending={acceptStatus === 'sending'}
        failed={acceptStatus === 'error'}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleAccept}
      />

      <AmbassadorWelcomeModal
        show={accepted}
        communityUrl={communityUrl}
        onClose={handleCloseWelcome}
      />
    </Container>
  );
};

export default AmbassadorInvitation;
